import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import {
  CITIES,
  INTERESTS,
  LANGUAGES,
  type City,
  type Interest,
  type Language,
} from "@nifgash/shared/types";
import {
  ageSchema,
  fullNameSchema,
  type ProfileInput,
} from "@nifgash/shared/validators";
import { Avatar } from "../components/Avatar";
import { Badge } from "../components/Badge";
import { Button } from "../components/Button";
import { Chip } from "../components/Chip";
import { ErrorBanner } from "../components/ErrorBanner";
import { Input } from "../components/Input";
import { Loader } from "../components/Loader";
import { Sheet } from "../components/Sheet";
import { tg } from "../lib/telegram";
import { applyTheme, loadThemePref, saveThemePref, type ThemePref } from "../lib/theme";
import {
  getMyHistory,
  getProfile,
  getPublicProfile,
  updateProfile,
  uploadPhoto,
  type PastEvent,
  type ProfilePayload,
  type PublicProfilePayload,
} from "../api/profile";
import { formatStartsAt } from "../lib/format";
import { getReferral, type ReferralPayload } from "../api/points";
import { getMyTickets, type MyTicketListItem } from "../api/tickets";
import { getSessionUser, setSessionUser, type UserRow } from "../api/session";

type EditField = "name" | "age" | "city" | "interests" | "languages" | null;

export function Profile() {
  const { id } = useParams<{ id: string }>();
  const sessionUser = getSessionUser();

  // Public mode: viewing someone else's profile (no edit, no settings).
  if (id && id !== sessionUser?.id) {
    return <PublicProfileView id={id} />;
  }

  return <OwnProfile />;
}

function OwnProfile() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [data, setData] = useState<ProfilePayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<EditField>(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [theme, setTheme] = useState<ThemePref>(() => loadThemePref());
  const [inviteOpen, setInviteOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [ticketsOpen, setTicketsOpen] = useState(false);
  const photoInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    getProfile()
      .then(setData)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  useEffect(() => {
    const app = tg();
    app?.MainButton.hide();
    if (editing === null) app?.BackButton.hide();
  }, [editing]);

  if (error) return <ErrorBanner message={t("profile.errorLoad", { error })} />;
  if (!data) return <Loader text={t("profile.loading")} full />;

  const u = data.user;
  const sinceMonth = u.created_at ? formatSince(u.created_at, i18n.language) : null;

  function applyUserUpdate(updated: UserRow) {
    setData((prev) => (prev ? { ...prev, user: updated } : prev));
    setSessionUser(updated);
    tg()?.HapticFeedback?.impactOccurred("light");
  }

  async function handlePhotoFile(file: File) {
    if (file.size > 5 * 1024 * 1024) {
      tg()?.showAlert(t("profile.photoTooLarge"));
      return;
    }
    setPhotoUploading(true);
    try {
      const res = await uploadPhoto(file);
      setData((prev) =>
        prev
          ? { ...prev, photo_url: res.photo_url, user: { ...prev.user, photo_path: res.photo_path } }
          : prev,
      );
      tg()?.HapticFeedback?.impactOccurred("light");
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      tg()?.showAlert(t("profile.uploadFailed", { msg }));
    } finally {
      setPhotoUploading(false);
    }
  }

  return (
    <>
      <div className="min-h-screen bg-ink-50 pb-32">
        <div
          className="relative px-5 pt-10 pb-8"
          style={{
            background:
              "radial-gradient(ellipse 100% 70% at 50% 0%, var(--color-brand-purple-soft) 0%, transparent 70%), var(--color-surface)",
          }}
        >
          <div className="flex flex-col items-center">
            <button
              type="button"
              onClick={() => photoInputRef.current?.click()}
              disabled={photoUploading}
              aria-label={t("profile.changePhotoAria")}
              className="relative active:scale-95 transition-transform"
            >
              <Avatar url={data.photo_url} name={u.full_name} size={104} />
              <div className="absolute bottom-0 right-0 w-9 h-9 rounded-full bg-brand-purple text-white flex items-center justify-center shadow-[var(--shadow-card)]">
                {photoUploading ? <DotIcon /> : <CamIcon />}
              </div>
            </button>
            <input
              ref={photoInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void handlePhotoFile(f);
                e.target.value = "";
              }}
            />
            <div className="mt-4 flex items-center gap-2">
              <h1 className="text-[24px] font-extrabold text-ink-900 tracking-tight">
                {u.full_name ?? t("profile.noName")}
              </h1>
              {data.verified && (
                <Badge tone="purple" icon={<CheckIcon />}>
                  {t("profile.badgeVerified")}
                </Badge>
              )}
            </div>
            {u.age != null && (
              <div className="text-[14px] text-ink-500 mt-0.5">{t("profile.ageYears", { count: u.age })}</div>
            )}
          </div>

          <div className="mt-6 grid grid-cols-3 gap-2 max-w-[420px] mx-auto">
            <StatCell
              label={t("profile.stats.meetings")}
              value={String(data.attended)}
              onClick={() => setHistoryOpen(true)}
            />
            <StatCell
              label={t("profile.stats.points")}
              value={String(u.points_balance ?? 0)}
              onClick={() => navigate("/points")}
              accent
            />
            <StatCell label={t("profile.stats.withUs")} value={sinceMonth ?? "—"} />
          </div>
        </div>

        <div className="px-5 mt-6 space-y-3">
          <PremiumCard premiumUntil={u.premium_until} onClick={() => navigate("/premium")} />
          <button
            onClick={() => setTicketsOpen(true)}
            className="w-full p-4 rounded-[var(--radius-md)] bg-surface border border-ink-100 flex items-center gap-3 active:scale-[0.99] transition-transform"
          >
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-brand-blue-soft flex items-center justify-center text-brand-blue">
              <TicketIcon />
            </div>
            <div className="flex-1 text-left">
              <div className="text-[15px] font-bold text-ink-900">{t("profile.myTickets")}</div>
              <div className="text-[13px] text-ink-500 mt-0.5">{t("profile.myTicketsHint")}</div>
            </div>
            <ChevronRight />
          </button>
          <button
            onClick={() => setInviteOpen(true)}
            className="w-full p-4 rounded-[var(--radius-md)] bg-brand-purple-soft border border-brand-purple/20 flex items-center gap-3 active:scale-[0.99] transition-transform"
          >
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-brand-purple flex items-center justify-center text-white">
              <GiftIcon />
            </div>
            <div className="flex-1 text-left">
              <div className="text-[15px] font-bold text-ink-900">{t("profile.inviteFriends")}</div>
              <div className="text-[13px] text-ink-700 mt-0.5">
                {t("profile.inviteFriendsHint")}
              </div>
            </div>
            <ChevronRight />
          </button>
        </div>

        <div className="px-5 mt-6 space-y-3">
          <FieldRow label={t("profile.fields.name")} value={u.full_name ?? "—"} onClick={() => setEditing("name")} />
          <FieldRow
            label={t("profile.fields.age")}
            value={u.age != null ? t("profile.ageYears", { count: u.age }) : "—"}
            onClick={() => setEditing("age")}
          />
          <FieldRow
            label={t("profile.fields.city")}
            value={u.city ? t(`labels.city.${u.city as City}`) : "—"}
            onClick={() => setEditing("city")}
          />
          <ChipsRow
            label={t("profile.fields.interests")}
            values={(u.interests ?? []) as Interest[]}
            translateKey={(v) => `labels.interest.${v}`}
            tone="yellow"
            onClick={() => setEditing("interests")}
          />
          <ChipsRow
            label={t("profile.fields.languages")}
            values={(u.languages ?? []) as Language[]}
            translateKey={(v) => `labels.language.${v}`}
            tone="blue"
            onClick={() => setEditing("languages")}
          />
        </div>

        <div className="px-5 mt-8 mb-3">
          <div className="text-[12px] font-bold uppercase tracking-[0.08em] text-ink-400">
            {t("profile.settings")}
          </div>
        </div>
        <div className="px-5 space-y-3">
          <ThemeToggleRow
            value={theme}
            onChange={(t) => {
              setTheme(t);
              saveThemePref(t);
              applyTheme(t);
              tg()?.HapticFeedback?.impactOccurred("light");
            }}
          />
        </div>
      </div>

      <NameSheet
        open={editing === "name"}
        user={u}
        onClose={() => setEditing(null)}
        onSaved={(up) => {
          applyUserUpdate(up);
          setEditing(null);
        }}
      />
      <AgeSheet
        open={editing === "age"}
        user={u}
        onClose={() => setEditing(null)}
        onSaved={(up) => {
          applyUserUpdate(up);
          setEditing(null);
        }}
      />
      <CitySheet
        open={editing === "city"}
        user={u}
        onClose={() => setEditing(null)}
        onSaved={(up) => {
          applyUserUpdate(up);
          setEditing(null);
        }}
      />
      <InterestsSheet
        open={editing === "interests"}
        user={u}
        onClose={() => setEditing(null)}
        onSaved={(up) => {
          applyUserUpdate(up);
          setEditing(null);
        }}
      />
      <LanguagesSheet
        open={editing === "languages"}
        user={u}
        onClose={() => setEditing(null)}
        onSaved={(up) => {
          applyUserUpdate(up);
          setEditing(null);
        }}
      />
      <InviteSheet open={inviteOpen} onClose={() => setInviteOpen(false)} />
      <HistorySheet open={historyOpen} onClose={() => setHistoryOpen(false)} />
      <TicketsSheet open={ticketsOpen} onClose={() => setTicketsOpen(false)} />
    </>
  );
}

type SheetProps = {
  open: boolean;
  user: UserRow;
  onClose: () => void;
  onSaved: (u: UserRow) => void;
};

function buildPayload(user: UserRow, patch: Partial<ProfileInput>): ProfileInput {
  return {
    full_name: user.full_name ?? "",
    age: user.age ?? 16,
    city: ((user.city as City) ?? "haifa") as City,
    interests: ((user.interests ?? []) as Interest[]).length
      ? ((user.interests ?? []) as Interest[])
      : (INTERESTS.slice(0, 1) as Interest[]),
    languages: ((user.languages ?? []) as Language[]).length
      ? ((user.languages ?? []) as Language[])
      : (LANGUAGES.slice(0, 1) as Language[]),
    ...patch,
  };
}

function useSheetSave(user: UserRow, onSaved: (u: UserRow) => void) {
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  async function save(patch: Partial<ProfileInput>) {
    setErr(null);
    setSaving(true);
    try {
      const updated = await updateProfile(buildPayload(user, patch));
      onSaved(updated);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      tg()?.HapticFeedback?.impactOccurred("medium");
    } finally {
      setSaving(false);
    }
  }
  return { save, saving, err, setErr };
}

function NameSheet({ open, user, onClose, onSaved }: SheetProps) {
  const { t } = useTranslation();
  const [value, setValue] = useState(user.full_name ?? "");
  const { save, saving, err } = useSheetSave(user, onSaved);
  useEffect(() => {
    if (open) setValue(user.full_name ?? "");
  }, [open, user.full_name]);

  function submit() {
    const parsed = fullNameSchema.safeParse(value);
    if (!parsed.success) {
      tg()?.showAlert(parsed.error.issues[0]?.message ?? t("profile.invalidName"));
      return;
    }
    void save({ full_name: parsed.data });
  }

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={t("profile.sheets.name")}
      footer={
        <Button stretched size="lg" onClick={submit} disabled={saving}>
          {saving ? t("common.saving") : t("common.save")}
        </Button>
      }
    >
      {err && <ErrorBanner message={err} />}
      <Input value={value} autoFocus onChange={(e) => setValue(e.target.value)} placeholder={t("profile.sheets.name")} />
    </Sheet>
  );
}

function AgeSheet({ open, user, onClose, onSaved }: SheetProps) {
  const { t } = useTranslation();
  const [value, setValue] = useState(user.age != null ? String(user.age) : "");
  const { save, saving, err } = useSheetSave(user, onSaved);
  useEffect(() => {
    if (open) setValue(user.age != null ? String(user.age) : "");
  }, [open, user.age]);

  function submit() {
    const n = Number(value);
    if (!Number.isFinite(n)) {
      tg()?.showAlert(t("profile.invalidAgeNumber"));
      return;
    }
    const parsed = ageSchema.safeParse(n);
    if (!parsed.success) {
      tg()?.showAlert(parsed.error.issues[0]?.message ?? t("profile.invalidAge"));
      return;
    }
    void save({ age: parsed.data });
  }

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={t("profile.sheets.age")}
      footer={
        <Button stretched size="lg" onClick={submit} disabled={saving}>
          {saving ? t("common.saving") : t("common.save")}
        </Button>
      }
    >
      {err && <ErrorBanner message={err} />}
      <Input
        type="number"
        inputMode="numeric"
        value={value}
        autoFocus
        onChange={(e) => setValue(e.target.value)}
        placeholder={t("profile.sheets.agePlaceholder")}
      />
    </Sheet>
  );
}

function CitySheet({ open, user, onClose, onSaved }: SheetProps) {
  const { t } = useTranslation();
  const [value, setValue] = useState<City | null>((user.city as City) ?? null);
  const { save, saving, err } = useSheetSave(user, onSaved);
  useEffect(() => {
    if (open) setValue((user.city as City) ?? null);
  }, [open, user.city]);

  function submit() {
    if (!value) {
      tg()?.showAlert(t("profile.pickCity"));
      return;
    }
    void save({ city: value });
  }

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={t("profile.sheets.city")}
      footer={
        <Button stretched size="lg" onClick={submit} disabled={saving}>
          {saving ? t("common.saving") : t("common.save")}
        </Button>
      }
    >
      {err && <ErrorBanner message={err} />}
      <div className="flex flex-wrap gap-2">
        {CITIES.map((c) => (
          <Chip key={c} size="md" selected={value === c} onClick={() => setValue(c)}>
            {t(`labels.city.${c}`)}
          </Chip>
        ))}
      </div>
    </Sheet>
  );
}

function InterestsSheet({ open, user, onClose, onSaved }: SheetProps) {
  const { t } = useTranslation();
  const [values, setValues] = useState<Interest[]>(((user.interests ?? []) as Interest[]));
  const { save, saving, err } = useSheetSave(user, onSaved);
  useEffect(() => {
    if (open) setValues((user.interests ?? []) as Interest[]);
  }, [open, user.interests]);

  function toggle(v: Interest) {
    setValues((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]));
  }

  function submit() {
    if (values.length === 0) {
      tg()?.showAlert(t("profile.pickInterest"));
      return;
    }
    void save({ interests: values });
  }

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={t("profile.sheets.interests")}
      footer={
        <Button stretched size="lg" onClick={submit} disabled={saving}>
          {saving ? t("common.saving") : t("common.save")}
        </Button>
      }
    >
      {err && <ErrorBanner message={err} />}
      <div className="flex flex-wrap gap-2">
        {INTERESTS.map((i) => (
          <Chip
            key={i}
            size="md"
            tone="yellow"
            selected={values.includes(i)}
            onClick={() => toggle(i)}
          >
            {t(`labels.interest.${i}`)}
          </Chip>
        ))}
      </div>
    </Sheet>
  );
}

function LanguagesSheet({ open, user, onClose, onSaved }: SheetProps) {
  const { t } = useTranslation();
  const [values, setValues] = useState<Language[]>(((user.languages ?? []) as Language[]));
  const { save, saving, err } = useSheetSave(user, onSaved);
  useEffect(() => {
    if (open) setValues((user.languages ?? []) as Language[]);
  }, [open, user.languages]);

  function toggle(v: Language) {
    setValues((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]));
  }

  function submit() {
    if (values.length === 0) {
      tg()?.showAlert(t("profile.pickLang"));
      return;
    }
    void save({ languages: values });
  }

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={t("profile.sheets.languages")}
      footer={
        <Button stretched size="lg" onClick={submit} disabled={saving}>
          {saving ? t("common.saving") : t("common.save")}
        </Button>
      }
    >
      {err && <ErrorBanner message={err} />}
      <div className="flex flex-wrap gap-2">
        {LANGUAGES.map((l) => (
          <Chip
            key={l}
            size="md"
            tone="blue"
            selected={values.includes(l)}
            onClick={() => toggle(l)}
          >
            {t(`labels.language.${l}`)}
          </Chip>
        ))}
      </div>
    </Sheet>
  );
}

function ThemeToggleRow({
  value,
  onChange,
}: {
  value: ThemePref;
  onChange: (t: ThemePref) => void;
}) {
  const { t } = useTranslation();
  // Effective scheme — what the user sees right now
  const isDark =
    value === "dark" ||
    (value === "auto" && document.documentElement.classList.contains("theme-dark"));

  function toggle() {
    onChange(isDark ? "light" : "dark");
  }

  return (
    <button
      onClick={toggle}
      className="w-full bg-surface rounded-[var(--radius-md)] p-4 border border-ink-100 flex items-center justify-between text-left active:scale-[0.99] transition-transform"
    >
      <div className="flex items-center gap-3">
        <span className={isDark ? "text-brand-purple" : "text-brand-yellow"}>
          {isDark ? <MoonIcon /> : <SunIcon />}
        </span>
        <div>
          <div className="text-[13px] font-semibold text-ink-500 uppercase tracking-wider">
            {t("profile.theme.label")}
          </div>
          <div className="text-[15px] font-semibold text-ink-900 mt-0.5">
            {isDark ? t("profile.theme.dark") : t("profile.theme.light")}
          </div>
        </div>
      </div>
      <Switch on={isDark} />
    </button>
  );
}

function Switch({ on }: { on: boolean }) {
  return (
    <div
      className={`
        w-12 h-7 rounded-full relative transition-colors duration-200
        ${on ? "bg-brand-purple" : "bg-ink-200"}
      `}
      aria-hidden
    >
      <div
        className={`
          absolute top-0.5 w-6 h-6 rounded-full bg-white shadow-[var(--shadow-card)]
          transition-all duration-200
          ${on ? "left-[22px]" : "left-0.5"}
        `}
        style={{ transitionTimingFunction: "var(--ease-out)" }}
      />
    </div>
  );
}

function SunIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}

function formatSince(iso: string, lang: string): string {
  const d = new Date(iso);
  // Use the active language's short-month name so Hebrew/English/Russian all
  // render natively. Intl.DateTimeFormat takes care of the localisation —
  // far less brittle than maintaining hand-translated month arrays.
  try {
    return new Intl.DateTimeFormat(lang, { month: "short", year: "numeric" }).format(d);
  } catch {
    return d.toLocaleDateString();
  }
}

function StatCell({
  label,
  value,
  onClick,
  accent,
}: {
  label: string;
  value: string;
  onClick?: () => void;
  accent?: boolean;
}) {
  const cls = `
    rounded-[var(--radius-lg)] p-4 shadow-[var(--shadow-card)]
    border text-center transition-transform
    ${accent ? "bg-brand-yellow-soft border-brand-yellow/30" : "bg-surface border-ink-100"}
    ${onClick ? "cursor-pointer active:scale-[0.97]" : ""}
  `;
  const numCls = `text-[22px] font-extrabold leading-none tracking-tight ${accent ? "text-brand-yellow" : "text-ink-900"}`;
  const labelCls = "text-[11px] font-semibold uppercase tracking-wider mt-1.5 text-ink-500";

  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={cls}>
        <div className={numCls}>{value}</div>
        <div className={labelCls}>{label}</div>
      </button>
    );
  }
  return (
    <div className={cls}>
      <div className={numCls}>{value}</div>
      <div className={labelCls}>{label}</div>
    </div>
  );
}

function FieldRow({
  label,
  value,
  onClick,
}: {
  label: string;
  value: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="w-full bg-surface rounded-[var(--radius-md)] p-4 border border-ink-100 flex items-center justify-between text-left active:scale-[0.99] transition-transform"
    >
      <div className="text-[13px] font-semibold text-ink-500 uppercase tracking-wider">{label}</div>
      <div className="flex items-center gap-2 min-w-0">
        <div className="text-[15px] font-semibold text-ink-900 truncate">{value}</div>
        <ChevronRight />
      </div>
    </button>
  );
}

function ChipsRow<T extends string>({
  label,
  values,
  translateKey,
  tone,
  onClick,
}: {
  label: string;
  values: T[];
  translateKey: (v: T) => string;
  tone: "yellow" | "purple" | "blue";
  onClick: () => void;
}) {
  const { t } = useTranslation();
  return (
    <button
      onClick={onClick}
      className="w-full bg-surface rounded-[var(--radius-md)] p-4 border border-ink-100 text-left active:scale-[0.99] transition-transform"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="text-[13px] font-semibold text-ink-500 uppercase tracking-wider">{label}</div>
        <ChevronRight />
      </div>
      {values.length === 0 ? (
        <div className="text-[15px] text-ink-400">—</div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {values.map((v) => (
            <Chip key={v} size="sm" tone={tone} selected={false}>
              {t(translateKey(v))}
            </Chip>
          ))}
        </div>
      )}
    </button>
  );
}

function ChevronRight() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" className="text-ink-300 flex-shrink-0">
      <path d="M9 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function PremiumCard({
  premiumUntil,
  onClick,
}: {
  premiumUntil: string | null;
  onClick: () => void;
}) {
  const { t, i18n } = useTranslation();
  const active = premiumUntil != null && new Date(premiumUntil).getTime() > Date.now();
  if (active) {
    const until = new Date(premiumUntil!).toLocaleDateString(i18n.language, {
      day: "numeric",
      month: "long",
    });
    return (
      <button
        onClick={onClick}
        className="w-full p-4 rounded-[var(--radius-md)] bg-brand-green-soft border border-brand-green/20 flex items-center gap-3 active:scale-[0.99] transition-transform"
      >
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-brand-green flex items-center justify-center text-white">
          <StarFilledIcon />
        </div>
        <div className="flex-1 text-left">
          <div className="text-[15px] font-bold text-ink-900">{t("profile.premium.activeTitle")}</div>
          <div className="text-[13px] text-ink-700 mt-0.5">{t("profile.premium.activeSubtitle", { date: until })}</div>
        </div>
        <ChevronRight />
      </button>
    );
  }
  return (
    <button
      onClick={onClick}
      className="w-full p-4 rounded-[var(--radius-md)] bg-gradient-to-r from-brand-purple to-brand-blue text-white flex items-center gap-3 shadow-[var(--shadow-card)] active:scale-[0.99] transition-transform"
    >
      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
        <StarFilledIcon />
      </div>
      <div className="flex-1 text-left">
        <div className="text-[15px] font-bold">{t("profile.premium.inactiveTitle")}</div>
        <div className="text-[13px] text-white/85 mt-0.5">
          {t("profile.premium.inactiveSubtitle")}
        </div>
      </div>
      <ChevronRight />
    </button>
  );
}

function StarFilledIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
    </svg>
  );
}

function GiftIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 12 20 22 4 22 4 12" />
      <rect x="2" y="7" width="20" height="5" />
      <line x1="12" y1="22" x2="12" y2="7" />
      <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" />
      <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
    </svg>
  );
}

function InviteSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const [data, setData] = useState<ReferralPayload | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCopied(false);
    getReferral().then(setData).catch(() => setData(null));
  }, [open]);

  function copy() {
    if (!data) return;
    void navigator.clipboard?.writeText(data.link).then(
      () => {
        setCopied(true);
        tg()?.HapticFeedback?.impactOccurred("light");
        setTimeout(() => setCopied(false), 2000);
      },
      () => {
        // Fallback for clipboard-disabled contexts — keep silent UX
      },
    );
  }

  function share() {
    if (!data) return;
    const text = t("profile.invite.text");
    const url = `https://t.me/share/url?url=${encodeURIComponent(data.link)}&text=${encodeURIComponent(text)}`;
    tg()?.HapticFeedback?.impactOccurred("light");
    tg()?.openTelegramLink(url);
  }

  return (
    <Sheet open={open} onClose={onClose} title={t("profile.invite.title")}>
      <div className="space-y-4">
        <div className="text-[14px] text-ink-700 leading-relaxed">
          {t("profile.invite.text")}
        </div>

        {data && (
          <>
            <div className="grid grid-cols-2 gap-2">
              <StatCell label={t("profile.invite.registered")} value={String(data.stats.total)} />
              <StatCell label={t("profile.invite.attended")} value={String(data.stats.active)} />
            </div>

            <div className="bg-ink-50 rounded-[var(--radius-md)] p-3 border border-ink-100 flex items-center gap-2">
              <div className="flex-1 text-[13px] text-ink-700 font-mono truncate">
                {data.link.replace(/^https?:\/\//, "")}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <Button size="md" variant="secondary" onClick={copy}>
                {copied ? t("common.copied") : t("common.copy")}
              </Button>
              <Button size="md" variant="primary" onClick={share}>
                {t("common.share")}
              </Button>
            </div>
          </>
        )}

        {!data && (
          <div className="text-[14px] text-ink-500 text-center py-4">
            {t("profile.invite.loading")}
          </div>
        )}
      </div>
    </Sheet>
  );
}

function HistorySheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const [events, setEvents] = useState<PastEvent[] | null>(null);

  useEffect(() => {
    if (!open) return;
    setEvents(null);
    getMyHistory().then((r) => setEvents(r.events)).catch(() => setEvents([]));
  }, [open]);

  return (
    <Sheet open={open} onClose={onClose} title={t("profile.history.title")}>
      {events === null ? (
        <div className="text-[14px] text-ink-500 text-center py-6">{t("profile.history.loading")}</div>
      ) : events.length === 0 ? (
        <div className="text-[14px] text-ink-500 text-center py-6">{t("profile.history.empty")}</div>
      ) : (
        <div className="space-y-2">
          {events.map((e) => (
            <div key={e.id} className="flex items-start gap-3 p-3 rounded-[var(--radius-md)] bg-ink-50">
              <div className="flex-1 min-w-0">
                <div className="text-[15px] font-semibold text-ink-900 truncate">{e.title}</div>
                <div className="text-[13px] text-ink-500 mt-0.5">
                  {formatStartsAt(e.starts_at)}
                  {e.format ? ` · ${t(`labels.format.${e.format}`, { defaultValue: e.format })}` : ""}
                </div>
                <div className="text-[13px] text-ink-500 truncate">
                  {e.venue_name}{e.city ? `, ${t(`labels.city.${e.city}`, { defaultValue: e.city })}` : ""}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Sheet>
  );
}

function TicketsSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState<MyTicketListItem[] | null>(null);

  useEffect(() => {
    if (!open) return;
    setTickets(null);
    getMyTickets().then(setTickets).catch(() => setTickets([]));
  }, [open]);

  return (
    <Sheet open={open} onClose={onClose} title={t("profile.tickets.title")}>
      {tickets === null ? (
        <div className="text-[14px] text-ink-500 text-center py-6">{t("profile.tickets.loading")}</div>
      ) : tickets.length === 0 ? (
        <div className="text-[14px] text-ink-500 text-center py-6">{t("profile.tickets.empty")}</div>
      ) : (
        <div className="space-y-2">
          {tickets.map((tk) => (
            <button
              key={tk.event_id}
              onClick={() => {
                onClose();
                navigate(`/events/${tk.event_id}/ticket`);
              }}
              className="w-full flex items-center gap-3 p-3 rounded-[var(--radius-md)] bg-ink-50 text-left active:scale-[0.99] transition-transform"
            >
              <div className="flex-shrink-0 w-9 h-9 rounded-full bg-brand-blue-soft flex items-center justify-center text-brand-blue">
                <TicketIcon />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[15px] font-semibold text-ink-900 truncate">{tk.title}</div>
                <div className="text-[13px] text-ink-500 mt-0.5">
                  {formatStartsAt(tk.starts_at)}
                  {tk.city ? ` · ${t(`labels.city.${tk.city}`, { defaultValue: tk.city })}` : ""}
                </div>
              </div>
              {tk.status === "used" ? (
                <span className="text-[11px] font-bold text-brand-green flex-shrink-0">
                  {t("profile.tickets.used")}
                </span>
              ) : (
                <ChevronRight />
              )}
            </button>
          ))}
        </div>
      )}
    </Sheet>
  );
}

function TicketIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v1a2 2 0 0 0 0 4v1a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-1a2 2 0 0 0 0-4V9z" />
      <path d="M13 7v10" strokeDasharray="2 2" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 6L9 17l-5-5" />
    </svg>
  );
}

function CamIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
      <circle cx="12" cy="13" r="4" />
    </svg>
  );
}

function DotIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <circle cx="6" cy="12" r="2" fill="currentColor">
        <animate attributeName="opacity" values="0;1;0" dur="1s" begin="0s" repeatCount="indefinite" />
      </circle>
      <circle cx="12" cy="12" r="2" fill="currentColor">
        <animate attributeName="opacity" values="0;1;0" dur="1s" begin="0.2s" repeatCount="indefinite" />
      </circle>
      <circle cx="18" cy="12" r="2" fill="currentColor">
        <animate attributeName="opacity" values="0;1;0" dur="1s" begin="0.4s" repeatCount="indefinite" />
      </circle>
    </svg>
  );
}

function PublicProfileView({ id }: { id: string }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [data, setData] = useState<PublicProfilePayload | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getPublicProfile(id)
      .then(setData)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, [id]);

  if (error) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center px-6 text-center">
        <div>
          <div className="text-[14px] text-brand-red mb-3">{error}</div>
          <Button variant="secondary" onClick={() => navigate("/")}>{t("eventDetail.toHome")}</Button>
        </div>
      </div>
    );
  }
  if (!data) return <Loader text={t("profile.loading")} full />;

  const u = data.user;
  return (
    <div className="min-h-screen bg-ink-50">
      <header className="px-5 pt-6 pb-4 border-b border-ink-100 sticky top-0 z-10 bg-surface/95 backdrop-blur">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="text-[13px] text-ink-500 hover:text-ink-900"
        >
          {t("eventDetail.back")}
        </button>
      </header>

      <div
        className="relative px-5 pt-10 pb-8 text-center"
        style={{
          background:
            "radial-gradient(ellipse 100% 70% at 50% 0%, var(--color-brand-purple-soft) 0%, transparent 70%), var(--color-surface)",
        }}
      >
        <div className="flex justify-center">
          <Avatar url={data.photo_url} name={u.full_name} size={104} />
        </div>
        <div className="mt-4 flex items-center gap-2 justify-center">
          <h1 className="text-[24px] font-extrabold text-ink-900 tracking-tight">
            {u.full_name ?? t("profile.noName")}
          </h1>
          {data.verified && (
            <Badge tone="purple">{t("profile.badgeVerified")}</Badge>
          )}
        </div>
        {u.age != null && (
          <div className="text-[14px] text-ink-500 mt-0.5">{t("profile.ageYears", { count: u.age })}</div>
        )}
        <div className="text-[14px] text-ink-500 mt-1">
          {data.attended > 0
            ? t("profile.publicAttended", { count: data.attended })
            : t("profile.publicEmptyAttended")}
        </div>
      </div>

      <div className="px-5 mt-6 space-y-3 max-w-[520px] mx-auto">
        <PublicSection title={t("profile.fields.interests")}>
          {(u.interests ?? []).length === 0 ? (
            <span className="text-[14px] text-ink-400">—</span>
          ) : (
            <div className="flex flex-wrap gap-2">
              {(u.interests as Interest[]).map((i) => (
                <Chip key={i} size="sm" tone="yellow" selected={false}>
                  {t(`labels.interest.${i}`)}
                </Chip>
              ))}
            </div>
          )}
        </PublicSection>

        <PublicSection title={t("profile.fields.languages")}>
          {(u.languages ?? []).length === 0 ? (
            <span className="text-[14px] text-ink-400">—</span>
          ) : (
            <div className="flex flex-wrap gap-2">
              {(u.languages as Language[]).map((l) => (
                <Chip key={l} size="sm" tone="blue" selected={false}>
                  {t(`labels.language.${l}`)}
                </Chip>
              ))}
            </div>
          )}
        </PublicSection>
      </div>

      <div className="h-16" />
    </div>
  );
}

function PublicSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-surface rounded-[var(--radius-md)] p-4 border border-ink-100">
      <div className="text-[13px] font-semibold text-ink-500 uppercase tracking-wider mb-3">
        {title}
      </div>
      {children}
    </div>
  );
}
